import React, { useState, useMemo } from "react";
import { Trash2, Network, Link2, Plus, X, ClipboardList, ListChecks, Search } from "lucide-react";
import { api } from "@/api";
import { toast } from "sonner";

const categoryColors = {
    person: "text-blue-400 border-blue-500/30",
    business: "text-amber-400 border-amber-500/30",
    location: "text-emerald-400 border-emerald-500/30",
    financial: "text-meye-fuchsia border-meye-fuchsia/30",
    medical: "text-red-400 border-red-500/30",
    errand: "text-cyan-400 border-cyan-500/30",
    family: "text-violet-400 border-violet-500/30",
    tool: "text-orange-400 border-orange-500/30",
    other: "text-zinc-400 border-meye-border",
};

export const GlossaryCard = ({ term, checklists = [], tasks = [], onDelete, onUpdated }) => {
    const catCls = categoryColors[term.category] || categoryColors.other;
    const [linking, setLinking] = useState(null); // "checklist" | "task" | null
    const [taskQuery, setTaskQuery] = useState("");

    const linkedChecklistIds = term.linked_checklist_ids || [];
    const linkedTaskIds = term.linked_task_ids || [];
    const linkedChecklists = checklists.filter((c) => linkedChecklistIds.includes(c.id));
    const linkedTasks = tasks.filter((t) => linkedTaskIds.includes(t.id));

    const patch = async (patchBody, msg) => {
        try {
            const upd = await api.updateGlossary(term.id, patchBody);
            onUpdated?.(upd);
            if (msg) toast.success(msg);
        } catch {
            toast.error("update failed");
        }
    };

    const linkChecklist = (id) => {
        const cl = checklists.find((c) => c.id === id);
        patch(
            { linked_checklist_ids: Array.from(new Set([...linkedChecklistIds, id])) },
            `linked "${cl?.title || "checklist"}" to ${term.term}`,
        );
        setLinking(null);
    };
    const unlinkChecklist = (id) => patch({ linked_checklist_ids: linkedChecklistIds.filter((x) => x !== id) });
    const linkTask = (id) => {
        const t = tasks.find((x) => x.id === id);
        patch(
            { linked_task_ids: Array.from(new Set([...linkedTaskIds, id])) },
            `linked "${(t?.text || "task").slice(0, 40)}" to ${term.term}`,
        );
        setLinking(null);
        setTaskQuery("");
    };
    const unlinkTask = (id) => patch({ linked_task_ids: linkedTaskIds.filter((x) => x !== id) });

    const unlinkedChecklists = checklists.filter((c) => !linkedChecklistIds.includes(c.id));
    const taskCandidates = useMemo(() => {
        const q = taskQuery.trim().toLowerCase();
        const filtered = tasks.filter(
            (t) => !linkedTaskIds.includes(t.id) && !t.completed && !t.archived,
        );
        if (!q) return filtered.slice(0, 20);
        return filtered.filter((t) => (t.text || "").toLowerCase().includes(q)).slice(0, 20);
    }, [tasks, taskQuery, linkedTaskIds]);

    return (
        <div className="bg-meye-card border border-meye-border rounded-md p-3.5 fade-up" data-testid={`glossary-card-${term.id}`}>
            <div className="flex items-start gap-3">
                <Network size={16} className="text-meye-cyan mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-brand text-sm uppercase tracking-wider text-zinc-100">{term.term}</h4>
                        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${catCls}`}>
                            {term.category || "other"}
                        </span>
                    </div>
                    {term.aliases?.length > 0 && (
                        <p className="text-xs text-zinc-500 mt-1 font-mono">aka {term.aliases.join(" · ")}</p>
                    )}
                    {term.notes && <p className="text-xs text-zinc-400 mt-1.5 whitespace-pre-wrap">{term.notes}</p>}

                    {/* Linked checklists */}
                    {linkedChecklists.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <ClipboardList size={11} className="text-meye-cyan" />
                            {linkedChecklists.map((cl) => (
                                <span
                                    key={cl.id}
                                    className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-meye-cyan/40 bg-meye-cyan/10 text-meye-cyan"
                                    data-testid={`glossary-linked-checklist-${cl.id}`}
                                >
                                    {cl.title}
                                    <button onClick={() => unlinkChecklist(cl.id)} className="hover:text-white" aria-label={`Unlink ${cl.title}`}>
                                        <X size={9} strokeWidth={2.5} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Linked tasks */}
                    {linkedTasks.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <ListChecks size={11} className="text-meye-fuchsia" />
                            {linkedTasks.map((t) => (
                                <span
                                    key={t.id}
                                    className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border border-meye-fuchsia/40 bg-meye-fuchsia/10 text-meye-fuchsia max-w-[240px]"
                                    data-testid={`glossary-linked-task-${t.id}`}
                                >
                                    <span className="truncate">{t.text}</span>
                                    <button onClick={() => unlinkTask(t.id)} className="hover:text-white flex-shrink-0" aria-label={`Unlink task`}>
                                        <X size={9} strokeWidth={2.5} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Checklist picker */}
                    {linking === "checklist" && (
                        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`glossary-checklist-picker-${term.id}`}>
                            {unlinkedChecklists.length === 0 ? (
                                <span className="text-[10px] text-zinc-500 font-mono">no more checklists to link</span>
                            ) : (
                                unlinkedChecklists.map((cl) => (
                                    <button
                                        key={cl.id}
                                        onClick={() => linkChecklist(cl.id)}
                                        className="text-[10px] font-brand font-medium uppercase tracking-widest px-2 py-1 rounded-full border border-meye-border bg-black/40 text-zinc-300 hover:border-meye-cyan hover:text-meye-cyan"
                                    >
                                        {cl.title}
                                    </button>
                                ))
                            )}
                            <button onClick={() => setLinking(null)} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-white px-1">cancel</button>
                        </div>
                    )}

                    {/* Task picker */}
                    {linking === "task" && (
                        <div className="mt-2 space-y-1.5" data-testid={`glossary-task-picker-${term.id}`}>
                            <div className="flex items-center gap-1.5 bg-black/60 border border-meye-border rounded px-2 py-1">
                                <Search size={11} className="text-zinc-500" />
                                <input
                                    autoFocus
                                    value={taskQuery}
                                    onChange={(e) => setTaskQuery(e.target.value)}
                                    placeholder="search tasks..."
                                    className="flex-1 bg-transparent text-xs text-zinc-100 focus:outline-none font-mono"
                                />
                                <button onClick={() => setLinking(null)} className="text-zinc-500 hover:text-white text-[10px] font-mono uppercase tracking-widest">
                                    cancel
                                </button>
                            </div>
                            <div className="max-h-40 overflow-y-auto space-y-1">
                                {taskCandidates.length === 0 ? (
                                    <p className="text-[10px] text-zinc-600 font-mono py-2">no tasks match</p>
                                ) : (
                                    taskCandidates.map((t) => (
                                        <button
                                            key={t.id}
                                            onClick={() => linkTask(t.id)}
                                            className="w-full text-left text-xs px-2 py-1 rounded border border-meye-border/50 bg-black/30 text-zinc-300 hover:border-meye-fuchsia/50 hover:text-meye-fuchsia truncate"
                                        >
                                            {t.text}
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Actions row */}
                    {!linking && (
                        <div className="mt-2 flex items-center gap-4 text-[10px] uppercase tracking-widest font-mono">
                            {unlinkedChecklists.length > 0 && (
                                <button
                                    onClick={() => setLinking("checklist")}
                                    className="text-zinc-500 hover:text-meye-cyan flex items-center gap-1"
                                    data-testid={`glossary-link-checklist-${term.id}`}
                                >
                                    <Plus size={10} strokeWidth={2.5} /> link checklist
                                </button>
                            )}
                            <button
                                onClick={() => setLinking("task")}
                                className="text-zinc-500 hover:text-meye-fuchsia flex items-center gap-1"
                                data-testid={`glossary-link-task-${term.id}`}
                            >
                                <Plus size={10} strokeWidth={2.5} /> link task
                            </button>
                        </div>
                    )}
                </div>
                <button
                    onClick={() => onDelete(term.id)}
                    className="text-zinc-600 hover:text-red-400"
                    data-testid={`glossary-delete-${term.id}`}
                >
                    <Trash2 size={13} strokeWidth={1.5} />
                </button>
            </div>
        </div>
    );
};

export default GlossaryCard;
